import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useRef } from 'react';
import type { Seo } from '@//app/api/seo/[id]';
import type { Market } from '@//config/domain-locale-map';
import useDynamicSeo from '@//data/seo/useDynamicSeo';
import useSeo from '@//data/seo/useSeo';

interface InitialSeo {
  id: string;
  seo: Seo;
}

interface InitialDynamicSeo {
  title?: string;
  ingress?: string;
  metaTitle?: string;
  metaDescription?: string;
}

interface UseResolvedSeoParams {
  seoId: string;
  market: Market;
  searchParams: URLSearchParams;
  vehicleSearchParams: URLSearchParams;
  searchSubscriptionId?: string;
  brandName: string;
  initialSeo?: InitialSeo;
  initialDynamicSeo?: InitialDynamicSeo | null;
}

interface ResolvedSeo {
  seo: Seo | null;
  fallbackTitle: string;
  fallbackDescription: string;
  fallbackMetaTitle: string;
  fallbackMetaDescription: string;
}

const useResolvedSeo = ({
  seoId,
  market,
  searchParams,
  vehicleSearchParams,
  searchSubscriptionId,
  brandName,
  initialSeo,
  initialDynamicSeo,
}: UseResolvedSeoParams): ResolvedSeo => {
  const t = useTranslations('pages.search');

  // Determine if we should fetch custom SEO (valid seoId and no subscription)
  const shouldFetchCustomSeo = !searchSubscriptionId && !!seoId;

  // Fetch custom SEO from API
  const seoQuery = useSeo(
    shouldFetchCustomSeo ? seoId : undefined,
    initialSeo && initialSeo.id === seoId && shouldFetchCustomSeo
      ? { initialData: initialSeo.seo }
      : undefined,
  );

  // Generate dynamic SEO values based on search parameters
  const dynamicSeo = useDynamicSeo({
    searchParams,
    market,
    brandName,
  });

  // Cache vehicle search params string to avoid repeated conversions
  const vehicleSearchParamsString = useMemo(
    () => vehicleSearchParams.toString(),
    [vehicleSearchParams],
  );

  const hasActiveSearch = vehicleSearchParamsString.length > 0;
  const isLoadingSeo = seoQuery.isLoading || seoQuery.isFetching;

  // Keep track of the previous hasActiveSearch state while loading
  const previousHasActiveSearchRef = useRef(hasActiveSearch);
  const stableHasActiveSearch = isLoadingSeo ? previousHasActiveSearchRef.current : hasActiveSearch;

  // Update the ref when not loading
  if (!isLoadingSeo) {
    previousHasActiveSearchRef.current = hasActiveSearch;
  }

  // Determine fallback values based on stable active search state
  const fallbackTitle = stableHasActiveSearch ? t('titleActiveSearch') : t('title');
  const fallbackDescription = stableHasActiveSearch
    ? t(`descriptionActiveSearch.${market}`)
    : t(`description.${market}`);

  const fallbackMetaTitle = stableHasActiveSearch
    ? t(`metadata.${market}.titleActiveSearch`)
    : t(`metadata.${market}.title`);
  const fallbackMetaDescription = stableHasActiveSearch
    ? t(`metadata.${market}.descriptionActiveSearch`)
    : t(`metadata.${market}.description`);

  // Merge SEO data with priority: Custom SEO > Dynamic SEO (client or server) > Fallback translations
  // Wait for customSeo query to complete before showing dynamic SEO to avoid double-swap
  // Only use seoQuery.data if the query is actually enabled (has a valid seoId)
  // Use server-provided initialDynamicSeo on first render, then client dynamicSeo for updates
  const effectiveDynamicSeo = dynamicSeo || initialDynamicSeo;

  // If we have server-provided dynamic SEO, the server already verified no custom SEO exists
  // So we can use it immediately without waiting for the client-side query to complete
  const canShowDynamicSeo = !!initialDynamicSeo || !isLoadingSeo;

  const currentSeo = useMemo(
    () =>
      (shouldFetchCustomSeo ? seoQuery.data : null) ||
      (canShowDynamicSeo && effectiveDynamicSeo
        ? {
            id: seoId,
            title: effectiveDynamicSeo.title || fallbackTitle,
            ingress: effectiveDynamicSeo.ingress || fallbackDescription,
            metaTitle: effectiveDynamicSeo.metaTitle || fallbackMetaTitle,
            metaDescription: effectiveDynamicSeo.metaDescription || fallbackMetaDescription,
            time: new Date().toISOString(),
            facetId: seoId,
          }
        : null),
    [
      seoQuery.data,
      shouldFetchCustomSeo,
      canShowDynamicSeo,
      effectiveDynamicSeo,
      seoId,
      fallbackTitle,
      fallbackDescription,
      fallbackMetaTitle,
      fallbackMetaDescription,
    ],
  );

  // Keep previous SEO stable while loading to prevent content swaps
  const stableSeoRef = useRef<typeof currentSeo>(currentSeo);
  const previousSearchParamsRef = useRef<string>(vehicleSearchParamsString);
  const previousManufacturerRef = useRef<string | null>(null);
  const previousHasDynamicSeoRef = useRef<boolean>(!!dynamicSeo);

  // Check if current search has dynamic SEO available (client or server)
  const hasDynamicSeo = !!effectiveDynamicSeo;

  // Memoized compatibility check function
  const isCompatibleSearch = useCallback(
    (prevParams: string, currentParams: string): boolean => {
      if (!prevParams || !currentParams) return false;

      // Fast path: if strings are identical, they're compatible
      if (prevParams === currentParams) return true;

      const prevManufacturer = previousManufacturerRef.current;
      const currentManufacturer = vehicleSearchParams.get('manufacturer');

      // If manufacturer changed, it's not compatible
      if (prevManufacturer && currentManufacturer && prevManufacturer !== currentManufacturer) {
        return false;
      }

      // If we previously had dynamic SEO but now we don't (e.g., added multiple values),
      // it's not compatible - we should show fallback, not stale specific SEO
      if (previousHasDynamicSeoRef.current && !hasDynamicSeo) {
        return false;
      }

      // If we're just adding filters to existing search, it's compatible
      return true;
    },
    [vehicleSearchParams, hasDynamicSeo],
  );

  const isSearchCompatible = isCompatibleSearch(
    previousSearchParamsRef.current,
    vehicleSearchParamsString,
  );

  // Update stable SEO when we have new data and we're not loading
  if (!isLoadingSeo) {
    // Update refs for tracking changes
    const shouldUpdateRefs = currentSeo || !isSearchCompatible;

    if (shouldUpdateRefs) {
      stableSeoRef.current = currentSeo; // Will be the new SEO or null
      previousSearchParamsRef.current = vehicleSearchParamsString;
      previousManufacturerRef.current = vehicleSearchParams.get('manufacturer');
      previousHasDynamicSeoRef.current = hasDynamicSeo;
    }
  }

  // Use stable SEO while loading only if the search is compatible
  const seo =
    isLoadingSeo && hasActiveSearch && isSearchCompatible ? stableSeoRef.current : currentSeo;

  return {
    seo,
    fallbackTitle,
    fallbackDescription,
    fallbackMetaTitle,
    fallbackMetaDescription,
  };
};

export default useResolvedSeo;
